import React, {
  Fragment, useEffect, useState, useRef, useMemo, memo,
} from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import {
  array, shape, object, string, number, func, bool, oneOf,
} from 'prop-types'

import useTracker from 'Pods/Shared/Hooks/useTracker'

import { httpErrorMessage, isSuccessStatus } from 'Utils/httpErrorMessage'
import ResolveProductPricing from 'Utils/ResolveProductPricing'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import {
  safeTrackSelectItem,
  safeTrackAddToCart,
  safeTrackRemoveFromCart,
  safeTrackAddToWishList,
  safeTrackSelectContent,
} from 'Utils/TrackingUtils'
import updatePageConfigCartData from 'Utils/UpdatePageConfigCart'

import ModelCardActionButtons from './Components/ModelCardActionButtons'
import ModelCardFormatTags from './Components/ModelCardFormatTags'
import ModelCardImage from './Components/ModelCardImage'
import ModelCardOverlay from './Components/ModelCardOverlay'
import ModelCardPrice from './Components/ModelCardPrice'
import ModelCardSponsoredTag from './Components/ModelCardSponsoredTag'
import ModelCardSubEligibleTag from './Components/ModelCardSubEligibleTag'
import ModelCardTypeTags from './Components/ModelCardTypeTags'
import ModelCardVerifiedTag from './Components/ModelCardVerifiedTag'
import { checkIfIsInTheSameLine, getPositionInLine } from './utils'
import ModelGallery from '../ModelGallery/ModelGallery'
import SubscriptionTag from './Components/SubscriptionTag'

function ModelCard({
  id,
  attributes,
  cart,
  modelIndex,
  listIndex,
  modelsPerRow,
  eagerImageCount,
  showGallery = null,
  onPreviewClick,
  onCloseGallery,
  lastModelIndex,
  viewerIsAdult = false,
  showAdultModal,
  currentSelectedFileTypes,
  setSelectedFilters,
  trackFilterChange,
  cardVisibilitySettings,
  cardSizeSetting,
  setCartItemIds,
  cartItemIds = EMPTY_ARRAY,
  wishListItems = EMPTY_ARRAY,
  setWishListItems,
  showCgtVerification,
  searchingFree = false,
  originalIndexes = EMPTY_ARRAY,
  originalSearchQuery,
  gaList,
  itemListId,
  isIOSDevice = false,
  isSkeletonComponent = false,
  extractingCoreStyles = false,
  trackLastItemInView,
  isSimilarSearchPossible = false,
  isSalesBoostEnabled = false,
  subscriptionBadgesEnabled = false,
  subscriptionFlowEnabled = false,
  premiumListingLabelEnabled = false,
}) {
  const pricing = useMemo(
    () => ResolveProductPricing({ id, attributes, cart }),
    [id, attributes, cart]
  )
  const isOnClientSide = typeof window !== 'undefined'
  const isOnWishlist = wishListItems.includes(Number(id))
  const [isCartActionLoading, setIsCartActionLoading] = useState(false)
  const [isWishListActionLoading, setIsWishListActionLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const articleRef = useRef(null)
  const cardRef = useRef(null)
  const { trackEvent } = useTracker()

  const isGalleryOpened = showGallery && showGallery.selectedModelId === id
  const {
    cgtStandard: isCgtStandardVisible,
    format: isFormatVisible,
    price: isPriceVisible,
    quickActionsOnHover: isQuickActionsOnHoverVisible,
    // Optional: pages without the quick-view gallery (e.g. SearchPage) hide the Preview action.
    preview: isPreviewVisible = true,
    type: isTypeVisible,
    title: isTitleVisible,
  } = cardVisibilitySettings || {}
  const cartItemId = (() => {
    const cartItem = cartItemIds.find(({ itemId }) => Number(itemId) === Number(id))
    return cartItem?.cartId
  })()

  const {
    imageAlt,
    title,
    primaryImage,
    metaverseFormatsList,
    metaverseFormatsCount,
    modelInfo,
    modelDownloadData,
    gaAttributes,
    categoryTitle,
    isCLDApplicable,
    isSaleOffApplicable,
    saleOffDiscount: initialSaleOffDiscount,
    subscriptionSubscribed,
    subscriptionPotential,
    subscriptionTierBand,
  } = attributes
  const {
    itemBrand,
    itemCategory,
    itemVariant,
    locationId,
    itemCategory2,
    itemCategory3,
    itemCategory4,
    itemCategory5,
    affiliation,
    isSponsored,
  } = gaAttributes || {}

  const isImageAgeRestricted = primaryImage.isAdultContent

  const {
    discount, originalPrice, price, ribbon,
  } = pricing

  const isLastElementOfTheLine = getPositionInLine({ modelIndex, modelsPerRow }) === 0
  const isLastElement = modelIndex === lastModelIndex
  const isFreeModel = discount === 0 && originalPrice === 0
  // Positive band whitelist: only show the label for a known valid band. A null/undefined band
  // (item over the premium price ceiling, or opted-in but no longer eligible) must NOT fall open -
  // premium is additionally gated by the rollout label flag.
  const isSubLabelAllowedForTier =
    subscriptionTierBand === 'essentials' ||
    (subscriptionTierBand === 'premium' && premiumListingLabelEnabled)
  const showSubEligibleTag =
    subscriptionFlowEnabled && subscriptionSubscribed && !isFreeModel && isSubLabelAllowedForTier

  // The open gallery hangs off the last card of the selected model's row, so which card anchors it
  // depends on modelsPerRow - which tracks the viewport. Derived per render rather than held in
  // state behind an effect: a resize used to leave the flag stale on every card, so after the row
  // width changed no card satisfied both isPreviewAnchor and the flag and the panel vanished.
  const isPreviewAnchor = isLastElementOfTheLine || isLastElement
  const isInTheSameLineOfSelectedModel = !!showGallery && isPreviewAnchor && checkIfIsInTheSameLine({
    modelsPerRow,
    selectedModelIndex: showGallery.selectedModelIndex,
    modelIndex,
    isLastElement,
  })

  const shouldDisplayPreview = showGallery
    && isOnClientSide
    && isPreviewAnchor
    && isInTheSameLineOfSelectedModel

  const originalIndex = originalIndexes.find(
    ({ id: originalId }) => Number(originalId) === Number(id)
  )?.index ?? Number(listIndex)
  const itemAttributes = {
    id: Number(id),
    title,
    price: originalPrice,
    searchQuery: originalSearchQuery || itemListId,
    isCLDApplicable,
    isSaleOffApplicable,
    saleOffDiscount: initialSaleOffDiscount,
    searchingFree,
    gaList,
    productType: itemVariant,
    category: categoryTitle,
    subcategory: itemCategory2,
    index: listIndex,
    itemBrand,
    itemCategory,
    itemCategories: {
      item_category2: itemCategory2,
      item_category3: itemCategory3,
      item_category4: itemCategory4,
      item_category5: itemCategory5,
    },
    affiliation,
    itemCategory3,
    itemCategory4,
    itemCategory5,
    itemVariant,
    locationId,
    originalIndex,
    discount: originalPrice - price,
  }

  const handlePreviewClick = () => {
    if (isImageAgeRestricted && !viewerIsAdult) {
      showAdultModal()
      return
    }

    onPreviewClick({
      selectedModelIndex: modelIndex,
      selectedModelId: id,
      selectedModelCartItemId: cartItemId,
      title,
      modelUrl: modelInfo.modelUrl,
      isFreeModel,
      isPurchased: !!modelDownloadData?.href,
      priceInfo: {
        discount,
        originalPrice,
        price,
        ribbon,
      },
      tags: metaverseFormatsList,
      itemAttributes,
    })

    safeTrackSelectContent(itemAttributes)
    trackEvent('card_preview_click', {
      model_id: id,
      item_list_id: itemAttributes.searchQuery || '',
    }, { customAttributes: { event_source: 'model_card' } })
  }

  useEffect(() => {
    const cardElement = cardRef.current
    if (!cardElement) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true)
          }
        })
      },
      {
        rootMargin: '50px',
        threshold: 0.1,
      }
    )

    observer.observe(cardElement)

    return () => {
      observer.unobserve(cardElement)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const card = cardRef.current

    if (!isGalleryOpened && isQuickActionsOnHoverVisible && !isIOSDevice) {
      card.classList.add('card-3d-model--quick-actions-on-hover-visible')
    } else {
      card.classList.remove('card-3d-model--quick-actions-on-hover-visible')
    }
  }, [isGalleryOpened, isQuickActionsOnHoverVisible, isIOSDevice, cardSizeSetting, viewerIsAdult])

  const scrollToPreview = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  const checkIfIsLoggedIn = () => {
    if (isOnClientSide && window.pageConfig && window.pageConfig.isCurrentUser) {
      return true
    }

    return false
  }

  const handleMount = async (ref) => {
    scrollToPreview(ref)

    const checkIfIsOnWishlist = async (selectedModelId) => {
      try {
        const wishlist = await window.fetchJSON('/api/internal/wish-lists/default')

        if (wishlist.data.length > 0) {
          return wishlist.data.some(
            (item) => item.attributes.itemId === Number(selectedModelId)
          )
        }

        return false
      } catch (error) {
        // to do: add error handling
        // once Sentry is available in listing page
        return false
      }
    }

    const { selectedModelId, isFreeModel: isFree } = showGallery

    if (!isFree && checkIfIsLoggedIn()) {
      checkIfIsOnWishlist(selectedModelId).then((_isOnWishlist) => {
        if (_isOnWishlist) {
          setWishListItems((state) => [...state, Number(selectedModelId)])
        } else {
          setWishListItems((state) => state.filter((item) => item !== Number(selectedModelId)))
        }
      })
    }
  }

  const handleAddToCart = ({
    modelId,
    attributesForTracking = itemAttributes,
    eventSource,
  }) => {
    if (isImageAgeRestricted && !viewerIsAdult) {
      showAdultModal()
      return
    }

    setIsCartActionLoading(true)

    const {
      searchQuery,
      index,
    } = attributesForTracking

    const payload = {
      authenticity_token: window.getToken(),
      index,
      item_list_id: searchQuery,
    }

    axios
      .post(`/api/internal/cg/items/${modelId}/cart_items`, payload, {
        withCredentials: true,
      })
      .then(({ data: { meta: cartData, data } }) => {
        // fire tracking events prior to updating the page config
        // to ensure the discount values are accurate
        safeTrackAddToCart(attributesForTracking, eventSource)
        safeTrackSelectItem(itemAttributes)

        // Update the page config with the new cart data
        updatePageConfigCartData(cartData, modelId)

        setCartItemIds(state => [
          ...state,
          { cartId: Number(data.attributes.id), itemId: Number(data.attributes.itemId) },
        ])

        // refresh cart widget in navigation.
        App.events.publish('app:cart:refresh')
        App.events.publish('top:menu:cart:open')

      })
      .catch(({ response: { data } }) => {
        window.showNotification(data.errors.detail, {
          type: 'error',
          autohide: true,
        })
      })
      .finally(() => {
        setIsCartActionLoading(false)
      })
  }

  const handleRemoveFromCart = async (modelId, attributesForTracking = itemAttributes) => {
    const modelCartItemId = cartItemIds.find(({ itemId }) => Number(itemId) === Number(modelId))?.cartId
    if (!modelId || !modelCartItemId) {
      return
    }

    setIsCartActionLoading(true)
    const payload = { authenticity_token: window.getToken() }
    axios
      .delete(`/api/internal/cg/items/${modelId}/cart_items/${modelCartItemId}`, payload, {
        withCredentials: true,
      })
      .then(({ data: { meta: cartData } }) => {
        const {
          minPurchaseAmountForCld: applicableCldValue,
          storeCreditsLeft: creditsAvailable,
          saleCampaignActive: isSaleOff,
          itemIds,
          saleOffColor,
          saleOffDiscount,
          saleOffTitle,
          size,
          totalPrice,
          finalPrice: value,
        } = cartData

        const updateLocalPageConfigCartData = () => {
          window.pageConfig.cart = {
            ...window.pageConfig.cart,
            applicableCldValue,
            creditsAvailable,
            isSaleOff,
            itemIds,
            saleOffColor,
            saleOffDiscount,
            saleOffTitle,
            size,
            totalPrice,
            value,
          }

          window.pageConfig.cart_items = window.pageConfig.cart_items.filter(
            (cartItem) => Number(cartItem.id) !== Number(modelId)
          )

          window.dispatchEvent(new Event('item-removed-from-cart'))

          // event to refresh cart data
          App.events.publish('app:cart:refresh')

          safeTrackRemoveFromCart(attributesForTracking)
        }

        updateLocalPageConfigCartData()
        window.showNotification(
          'Item was removed from cart',
          {
            type: 'success',
            autohide: true,
          }
        )
      })
      .catch(({ response: { data } }) => {
        window.showNotification(data.errors.detail, {
          type: 'error',
          autohide: true,
        })
      })
      .finally(() => {
        setIsCartActionLoading(false)
      })
  }

  const showWishlistError = (error) => {
    window.showNotification(httpErrorMessage(error, 'Failed to update wishlist. Please try again.'), {
      type: 'error',
      autohide: true,
    })
  }

  const getWishListId = async () => {
    try {
      const wishlists = await window.fetchJSON('/api/internal/wish-lists')

      if (wishlists.data.length > 0) {
        return wishlists.data[0].id
      }

      return null
    } catch (error) {
      // to do: add error handling
      // once Sentry is available in listing page
      return null
    }
  }

  const handleAddToWishlist = async ({
    modelId,
    attributesForTracking = itemAttributes,
    eventSource,
  }) => {
    if (isImageAgeRestricted && !viewerIsAdult) {
      showAdultModal()
      return
    }

    const createNewWishlist = async () => {
      const newWishListData = {
        data: {
          type: 'wishLists',
          attributes: { title: 'Default' },
        },
      }
      const {
        data: { id: newWishListId },
      } = await window.postJSON('/api/internal/wish-lists', newWishListData)
      return newWishListId
    }

    function addToWishList(wishListId, itemId) {
      const data = JSON.stringify({
        data: {
          type: 'wishListItems',
          attributes: {
            wish_list_id: wishListId,
            item_id: itemId,
          },
        },
      })
      window.performXHR(
        'post',
        `/api/internal/wish-lists/${wishListId}/items`,
        (responseText, status) => {
          setIsWishListActionLoading(false)

          // performXHR fires this callback for every completed request, success or not - the
          // edge throttles this endpoint (429), so without the status check a rejected add was
          // reported to the user as a success and the card flipped to "wishlisted".
          if (!isSuccessStatus(status)) {
            showWishlistError({ status })
            return
          }

          setWishListItems(state => [...state, Number(itemId)])
          safeTrackAddToWishList(attributesForTracking, eventSource)
          window.showNotification(
            'Model was successfully added to wishlist!',
            {
              type: 'success',
              autohide: true,
            }
          )
        },
        data
      )
    }

    setIsWishListActionLoading(true)

    let wishListId
    try {
      wishListId = (await getWishListId()) || (await createNewWishlist())
    } catch (error) {
      setIsWishListActionLoading(false)
      showWishlistError(error)
      return
    }

    addToWishList(
      wishListId,
      modelId || id,
      eventSource,
    )
  }

  const handleRemoveFromWishlist = async () => {
    setIsWishListActionLoading(true)
    const wishListId = await getWishListId()

    if (!wishListId) {
      setIsWishListActionLoading(false)
      showWishlistError()
      return
    }

    const itemId = showGallery ? showGallery.selectedModelId : id
    window.performXHR(
      'delete',
      `/api/internal/wish-lists/${wishListId}/items/0`,
      (responseText, status) => {
        setIsWishListActionLoading(false)

        if (!isSuccessStatus(status)) {
          showWishlistError({ status })
          return
        }

        setWishListItems(state => state.filter(item => item !== Number(itemId)))
        window.showNotification(
          'Model was successfully removed from wishlist!',
          {
            type: 'success',
            autohide: true,
          }
        )
      },
      JSON.stringify({ item_ids: itemId })
    )
  }

  const handleImageClick = (event) => {
    event.preventDefault()
    if (isImageAgeRestricted && !viewerIsAdult) {
      showAdultModal()
      return
    }

    safeTrackSelectItem(itemAttributes)

    if (event.button === 0 && (event.ctrlKey || event.metaKey)) {
      window.open(modelInfo.modelUrl, '_blank')
    } else {
      window.location = modelInfo.modelUrl
    }
  }

  const handleAuxImageClick = (event) => {
    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      safeTrackSelectItem(itemAttributes)
    }
  }

  const getHandler = (callback) => {
    return isFreeModel ? null : callback
  }

  const getGalleryHandler = (callback) => {
    return showGallery.isFreeModel ? null : callback
  }

  const handleModelCardOverlayClick = () => {
    if (isImageAgeRestricted && !viewerIsAdult) {
      showAdultModal()
    }
  }

  return (
    <Fragment>
      <div
        ref={cardRef}
        className={classnames(
          'card-3d-model js-fast-listing-item',
          `card-3d-model--${cardSizeSetting}`,
          isGalleryOpened && 'card-3d-model--gallery-opened',
          isImageAgeRestricted && !viewerIsAdult && 'card-3d-model--age-restricted',
        )}
        data-attributes={JSON.stringify(itemAttributes)}
      >
        {isIOSDevice && (
          <div
            className="card-3d-model--quick-actions-on-hover-visible-on-IOS"
            onClick={isGalleryOpened ? undefined : handleImageClick}
          />
        )}
        <article
          className="card-3d-model__article"
          data-model-id={id}
          onClick={isGalleryOpened ? onCloseGallery : undefined}
          ref={articleRef}
        >
          <ModelCardOverlay
            isGalleryOpened={isGalleryOpened}
            isImageAgeRestricted={isImageAgeRestricted && !viewerIsAdult}
            onClick={handleModelCardOverlayClick}
          />
          <ModelCardImage
            id={id}
            index={modelIndex}
            eagerImageCount={eagerImageCount}
            alt={imageAlt || title}
            // 4:3, matching the `padding-top: 75%` box the image is stretched into by
            // `object-fit: cover`. The new_grid_webp variant is a minimum-fill resize with no
            // crop, so its own pixel size varies with the source ratio and cannot be declared.
            width="612"
            height="459"
            onImageClick={isGalleryOpened ? undefined : handleImageClick}
            onAuxImageClick={isGalleryOpened ? undefined : handleAuxImageClick}
            image={primaryImage}
            productUrl={modelInfo.modelUrl}
            isTitleVisible={isTitleVisible}
            isSkeletonComponent={isSkeletonComponent}
            shouldTrackMobileTreshold={isLastElement}
            trackLastItemInView={trackLastItemInView}
          />
          <ModelCardFormatTags
            tags={metaverseFormatsList}
            tagsCount={metaverseFormatsCount}
            currentSelectedFileTypes={currentSelectedFileTypes}
            setSelectedFilters={setSelectedFilters}
            trackFilterChange={trackFilterChange}
            isVisible={isFormatVisible}
            isDiscountOrRibbonTagsActive={!!ribbon || !!discount}
            articleRef={articleRef}
          />
          <ModelCardPrice
            discount={discount}
            originalPrice={originalPrice}
            price={price}
            ribbon={ribbon}
            isVisible={isPriceVisible}
            modelId={id}
          />
          <div className={classnames(
            'card-3d-model__footer',
            isTitleVisible && 'card-3d-model__footer--with-title',
          )}
          >
            {showCgtVerification && (
              <ModelCardVerifiedTag
                isCgtVerified={modelInfo.isCgtVerified}
                isVisible={isCgtStandardVisible}
                isPrintReady={modelInfo.types.printReady}
              />
            )}
            {showSubEligibleTag && <ModelCardSubEligibleTag tierBand={subscriptionTierBand} />}
            <ModelCardTypeTags
              types={modelInfo.types}
              isVisible={isTypeVisible}
              hidePrintReady={showCgtVerification && modelInfo.isCgtVerified}
            />
          </div>
          {(!isSkeletonComponent && isVisible) && (
            <ModelCardActionButtons
              id={id}
              isLoggedIn={checkIfIsLoggedIn()}
              isCartActionLoading={isCartActionLoading}
              isFreeModel={isFreeModel}
              isOnCart={!!cartItemId}
              isOnWishlist={isOnWishlist}
              onAddToCart={getHandler((modelId) => {
                handleAddToCart({ modelId, eventSource: 'model card' })
              })}
              onRemoveFromCart={getHandler(() => handleRemoveFromCart(id))}
              isPurchased={!!modelDownloadData?.href}
              isWishListActionLoading={isWishListActionLoading}
              onAddToWishlist={getHandler((modelId) => {
                handleAddToWishlist({ modelId, eventSource: 'model card' })
              })}
              onPreviewClick={handlePreviewClick}
              onRemoveFromWishlist={getHandler(handleRemoveFromWishlist)}
              isGalleryOpened={isGalleryOpened}
              isPreviewVisible={isPreviewVisible}
              isTitleVisible={isTitleVisible}
              isSimilarSearchPossible={isSimilarSearchPossible}
              category={itemCategory}
            />
          )}
          {(isTitleVisible || extractingCoreStyles) && (
            <div className="card-3d-model__footer-title">
              <h3 className="card-3d-model__footer-title-content">
                <a
                  href={modelInfo.modelUrl}
                  onClick={isGalleryOpened ? undefined : handleImageClick}
                  onAuxClick={isGalleryOpened ? undefined : handleAuxImageClick}
                  className="js-track-click"
                  data-attributes={JSON.stringify({ id, item_index: modelIndex })}
                >
                  {title}
                </a>
              </h3>
              {subscriptionBadgesEnabled && subscriptionPotential && (
                <SubscriptionTag type="attention" />
              )}
              {((isSalesBoostEnabled && isSponsored) || extractingCoreStyles) && (
                <ModelCardSponsoredTag id={id} />
              )}
            </div>
          )}
        </article>
      </div>
      {shouldDisplayPreview && (
        <div className="col-12">
          <ModelGallery
            onCloseGallery={onCloseGallery}
            onMount={handleMount}
            onAddToCart={getGalleryHandler(() => handleAddToCart({
              modelId: showGallery.selectedModelId,
              itemAttributes: showGallery.itemAttributes,
              eventSource: 'model preview',
            }))}
            onAddToWishlist={getGalleryHandler(() => handleAddToWishlist({
              modelId: showGallery.selectedModelId,
              attributesForTracking: showGallery.itemAttributes,
              eventSource: 'model preview',
            }))}
            onRemoveFromCart={getGalleryHandler(() => handleRemoveFromCart(
              showGallery.selectedModelId,
              showGallery.itemAttributes,
            ))}
            onRemoveFromWishlist={getGalleryHandler(() => handleRemoveFromWishlist(showGallery.selectedModelId))}
            priceInfo={showGallery.priceInfo}
            title={showGallery.title}
            id={showGallery.selectedModelId}
            isWishListActionLoading={isWishListActionLoading}
            isCartActionLoading={isCartActionLoading}
            tags={showGallery.tags}
            currentSelectedFileTypes={currentSelectedFileTypes}
            setSelectedFilters={setSelectedFilters}
            trackFilterChange={trackFilterChange}
            cartItemIds={cartItemIds}
            wishListItems={wishListItems}
            isPurchased={showGallery.isPurchased}
            showCgtVerification={showCgtVerification}
            itemAttributes={showGallery.itemAttributes}
            modelUrl={showGallery.modelUrl}
          />
        </div>
      )}
    </Fragment>
  )
}

ModelCard.propTypes = {
  id: string.isRequired,
  attributes: shape({
    imageAlt: string,
    title: string,
    primaryImage: shape({
      gridUrl: string.isRequired,
    }).isRequired,
    metaverseFormatsList: array,
    metaverseFormatsCount: number,
    modelInfo: shape({
      isCgtVerified: bool.isRequired,
      modelUrl: string,
      types: object.isRequired,
    }),
  }).isRequired,
  cart: object.isRequired,
  modelIndex: number.isRequired,
  listIndex: number.isRequired,
  modelsPerRow: number.isRequired,
  eagerImageCount: number,
  showGallery: shape({
    selectedModelIndex: number.isRequired,
    selectedModelId: string.isRequired,
  }),
  onPreviewClick: func.isRequired,
  onCloseGallery: func.isRequired,
  lastModelIndex: number.isRequired,
  viewerIsAdult: bool,
  showAdultModal: func.isRequired,
  currentSelectedFileTypes: array.isRequired,
  setSelectedFilters: func.isRequired,
  trackFilterChange: func.isRequired,
  cardVisibilitySettings: shape({
    cgtStandard: bool.isRequired,
    format: bool.isRequired,
    price: bool.isRequired,
    quickActionsOnHover: bool.isRequired,
    preview: bool,
    type: bool.isRequired,
    title: bool.isRequired,
  }).isRequired,
  cardSizeSetting: oneOf(['small', 'medium', 'large']).isRequired,
  setCartItemIds: func.isRequired,
  cartItemIds: array,
  wishListItems: array,
  setWishListItems: func.isRequired,
  showCgtVerification: bool.isRequired,
  searchingFree: bool,
  originalIndexes: array,
  originalSearchQuery: string,
  gaList: string,
  itemListId: string,
  isIOSDevice: bool,
  isSkeletonComponent: bool,
  extractingCoreStyles: bool,
  trackLastItemInView: func.isRequired,
  isSimilarSearchPossible: bool,
  isSalesBoostEnabled: bool,
  subscriptionBadgesEnabled: bool,
  subscriptionFlowEnabled: bool,
  premiumListingLabelEnabled: bool,
}

export default memo(ModelCard)
